import { getConfirmedRoleDataFromCache } from 'src/components/Profile/CareerHistory/apolloCache.optimistic';
import { getUnconfirmedRolesFromCache } from 'src/components/Profile/CareerHistory/data';
import {
  CreateResumeActions,
  CreateStep,
} from 'src/components/Profile/CreateResume/reducer';
import {
  getConfirmedQualificationsFromCache,
  getUnconfirmedQualificationsFromCache,
} from 'src/components/Profile/Education/data';
import { getLicenceDataFromStore } from 'src/components/Profile/Licences/apolloCache.ts';
import type { GraphqlLicence } from 'src/components/Profile/Licences/types.ts';
import { PROFILE_INSIGHTS_LOADED } from 'src/components/Profile/ProfileInsights/actionCreators';
import {
  Actions as ProfileVisibilityActions,
  type ProfileVisibilityAction,
} from 'src/components/Profile/ProfileVisibility/actions';
import { PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED } from 'src/components/Profile/Resume/EditResume/ManageResumes/ResumeDetailItem/actionCreators';
import {
  PROFILE_SUGGESTIONS_CTA_PRESSED,
  PROFILE_SUGGESTIONS_LOADED,
} from 'src/components/Profile/Suggestions/actionCreators';
import { getCandidateIdFromCache } from 'src/components/Profile/data';
import { getUserDetailsFromCache } from 'src/handlers/analytics';
import {
  type createAnalytics,
  getProfileSharedStatus,
} from 'src/modules/analytics';
import type { ProfileState } from 'src/modules/analytics/reducers/profile';
import type {
  AnalyticsCurrentPageSectionState,
  AnalyticsLinkEvent,
  AnalyticsProfileVisibilityLevel,
  AnalyticsLinkPosition,
  AnalyticsHeaderFooterLinkProps,
  AnalyticsUpdateType,
  AnalyticsProfileSuggestionsProps,
  AnalyticsResumeAlertShownType,
  AnalyticsResumeAlertShownTone,
  AnalyticsResumeAlertPosition,
  AnalyticsResumeAlertShownMessage,
  AnalyticsResumeAlertTitle,
} from 'src/modules/analytics/types';
import type { CardOpenProps } from 'src/modules/analytics/types/cardOpened';
import { Actions as YearsOfExperienceActions } from 'src/modules/analytics/yearsOfExperience/actionCreators';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { AppConfig } from 'src/modules/appConfig/types';
import {
  COMPANY_REVIEWS_LINK_CLICKED,
  HEADER_FOOTER_LINK_CLICKED,
  TRACKED_LINK_CLICKED,
} from 'src/reducers/location';
import type { Action, ApiClient } from 'src/types';
import {
  GetApproachabilityDocument,
  GetProfileInsightsDocument,
  GetProfileVisibilityDocument,
  GetShareableProfileSettingsDocument,
  GetVerificationsDocument,
} from 'src/types/graphql';

import { instrumentAnalytics } from './analytics/analyticsMiddlewareUtils';
import { ProfileActions } from './analytics/profile/profile.ts';
import {
  calculateLicenceAnalytics,
  calculateQualificationAnalytics,
  calculateVerificationsAnalytics,
} from './utils';

type AnalyticsMiddlewareAction =
  | (Action<CreateResumeActions.UPDATE_CREATE_STEP> & {
      payload: { from: CreateStep; to: CreateStep };
    })
  | Extract<
      ProfileVisibilityAction,
      Action<ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START>
    >
  | (Action<typeof COMPANY_REVIEWS_LINK_CLICKED> & {
      payload: {
        companyName: string;
      };
    })
  | (Action<typeof TRACKED_LINK_CLICKED> & {
      payload: {
        analytics: {
          linkName: AnalyticsLinkEvent;
          thirdPartyLink: boolean;
          linkPosition: AnalyticsLinkPosition;
        };
        type: string;
      };
    })
  | (Action<typeof HEADER_FOOTER_LINK_CLICKED> & {
      payload: AnalyticsHeaderFooterLinkProps;
    })
  | Action<typeof PROFILE_INSIGHTS_LOADED>
  | (Action<typeof PROFILE_SUGGESTIONS_LOADED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | (Action<typeof PROFILE_SUGGESTIONS_CTA_PRESSED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | Action<CreateResumeActions.CREATE_RESUME_RENDERED>
  | Action<ProfileActions.PROFILE_RENDERED>
  | Action<ProfileVisibilityActions.PROFILE_VISIBILITY_OPENED>
  | (Action<YearsOfExperienceActions.YEARS_OF_EXPERIENCE_EDIT_LOADED> & {
      payload: {
        currentPageSectionState: AnalyticsCurrentPageSectionState;
      };
    })
  | (Action<typeof PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED> & {
      payload: {
        alertShownType: AnalyticsResumeAlertShownType;
        alertShownTone: AnalyticsResumeAlertShownTone;
        alertShownPosition: AnalyticsResumeAlertPosition;
        alertShownTitle: AnalyticsResumeAlertTitle;
        alertShownMessage: AnalyticsResumeAlertShownMessage;
      };
    })
  | (Action<YearsOfExperienceActions.YEARS_OF_EXPERIENCE_UPDATE> & {
      payload: {
        actionName: AnalyticsUpdateType;
        userIsNewToWorkforce: boolean;
      };
    });

export type Analytics = ReturnType<typeof createAnalytics>;

export type AnalyticsEditLoadedType = Pick<
  CardOpenProps,
  'currentPageSectionState' | 'customProps' | 'currentPageSubsection'
>;

export const createAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  { language: languageCode, showFeature, zone, isUnifiedSite }: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (() =>
    (next: (action: AnalyticsMiddlewareAction) => void) =>
    (action: AnalyticsMiddlewareAction) => {
      const {
        profileVisibilityUpdate,
        profileLoaded,
        resumeAlertViewed,
        profileInsightsAnalyticsTracker,
        profileSuggestionsAnalyticsTracker,
        createResumeLoaded,
        yearsOfExperienceEditLoaded,
        createResumeButtonClicked,
        linkClicked,
        headerFooterLinkClicked,
        companyReviewsClicked,
      } = analytics;

      // PROFILE_PAGE;

      const getProfileVisibilityLevelData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileVisibilityDocument,
          variables: { languageCode },
        });

      const getProfileApproachabilityData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetApproachabilityDocument,
        });

      const getShareableProfileSettingsData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetShareableProfileSettingsDocument,
          variables: {
            languageCode,
            zone,
          },
        });

      const getProfileInsightsData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileInsightsDocument,
          variables: {
            zone,
            languageCode,
            timezone: 'Australia/Melbourne',
          },
        });

      const getGlobalProfileStateTrackingData = (): ProfileState => {
        const candidateId = getCandidateIdFromCache(apolloClient.cache);

        const profileVisibilityLevelData = getProfileVisibilityLevelData();

        const level =
          profileVisibilityLevelData?.viewer?.profileVisibility?.level;

        const profileVisibilityLevel = (
          level ? level.toLowerCase() : 'not set'
        ) as AnalyticsProfileVisibilityLevel;

        const profileApproachabilityData = getProfileApproachabilityData();

        const approachable =
          profileApproachabilityData?.viewer?.approachability?.approachable ??
          true;

        const profileApproachabilityStatus = approachable ? 'on' : 'off';

        const shareableProfileSettings =
          getShareableProfileSettingsData()?.viewer?.shareableProfileSettings;

        const isShareableProfileCreated =
          Boolean(shareableProfileSettings?.url) || false;
        const isShareableProfileActive =
          shareableProfileSettings?.active || false;

        const profileSharedStatus = getProfileSharedStatus({
          isShareableProfileCreated,
          isShareableProfileActive,
        });

        const profileInsights =
          getProfileInsightsData()?.viewer?.profileInsights;

        return {
          profileVisibilityLevel,
          profileApproachabilityStatus,
          profileSharedStatus,
          candidateId: candidateId ?? undefined,
          profileViewedCount: profileInsights?.viewCount ?? 0,
          profileSearchAppearanceCount:
            profileInsights?.searchImpressionCount ?? 0,
        };
      };

      switch (action.type) {
        case CreateResumeActions.CREATE_RESUME_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );

          createResumeLoaded(
            {
              eventName: 'create_resume_displayed',
              currentPage: 'create resume',
            },
            {
              trackingId,
              _id,
              externalId: externalId || undefined,
              isUnifiedSite,
            },
          );

          break;
        }

        case ProfileActions.PROFILE_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );

          const licences = getLicenceDataFromStore({
            cache: apolloClient.cache,
            languageCode,
            zone,
          });

          const { unconfirmedRoles } = getUnconfirmedRolesFromCache(
            apolloClient,
            showFeature('resume-parsing'),
          );

          const { unconfirmedQualifications } =
            getUnconfirmedQualificationsFromCache({
              apolloClient,
              languageCode,
              zone,
              isUnifiedSite,
            });
          const { confirmedQualifications } =
            getConfirmedQualificationsFromCache({
              ...apolloClient,
              languageCode,
              showQualificationLevel2: showFeature('qualification-level-major'),
              zone,
              isUnifiedSite,
            });

          const getProfileVerificationsData = () =>
            getDataFromCache({
              cache: apolloClient.cache,
              query: GetVerificationsDocument,
              variables: {
                zone,
                locale: 'en-AU',
              },
            });

          const verifications =
            getProfileVerificationsData()?.viewer?.profileVerifications;

          const workHistory = getConfirmedRoleDataFromCache({
            cache: apolloClient.cache,
          });
          const profileCareerHistoryCount = workHistory?.length;

          const isResumeParsed =
            unconfirmedRoles.length > 0 || unconfirmedQualifications.length > 0;

          profileLoaded(getGlobalProfileStateTrackingData(), {
            trackingId,
            _id,
            externalId: externalId || undefined,
            isResumeParsed,
            isUnifiedSite,
            ...calculateLicenceAnalytics(licences as GraphqlLicence[]),
            ...(confirmedQualifications.length > 0
              ? calculateQualificationAnalytics(confirmedQualifications)
              : {}),
            ...calculateVerificationsAnalytics(
              verifications?.verified,
              verifications?.verifiable,
            ),
            profileCareerHistoryCount,
          });
          break;
        }

        case PROFILE_INSIGHTS_LOADED: {
          profileInsightsAnalyticsTracker({
            eventName: 'profile_insights_widget_loaded',
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case PROFILE_SUGGESTIONS_LOADED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_viewed',
            profileState: getGlobalProfileStateTrackingData(),
            props,
          });

          break;
        }
        case PROFILE_SUGGESTIONS_CTA_PRESSED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_pressed',
            props,
          });

          break;
        }

        case PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED: {
          const props = action.payload;
          resumeAlertViewed({
            ...props,
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case CreateResumeActions.UPDATE_CREATE_STEP: {
          const { to: newCreateStep } = action.payload;
          if (newCreateStep === CreateStep.PROFILE_REVIEW) {
            createResumeButtonClicked();
          }
          break;
        }

        case ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START: {
          const { level } = action.payload;

          const profileVisibilityLevel =
            level.toLowerCase() as AnalyticsProfileVisibilityLevel;

          profileVisibilityUpdate(profileVisibilityLevel);
          break;
        }

        case COMPANY_REVIEWS_LINK_CLICKED: {
          const { companyName } = action.payload;

          companyReviewsClicked({
            linkNameEventName: 'company_reviews_pressed',
            currentPageSection: 'career history',
            companyName,
          });

          break;
        }

        case TRACKED_LINK_CLICKED: {
          const {
            analytics: { linkName, linkPosition, thirdPartyLink },
            type: linkType,
          } = action.payload;
          linkClicked({
            linkNameEventName: linkName,
            linkPosition,
            thirdPartyLink,
            type: linkType,
          });
          break;
        }

        case HEADER_FOOTER_LINK_CLICKED: {
          const { linkPosition, linkText, linkAction } = action.payload;

          headerFooterLinkClicked({
            linkNameEventName: 'navigation_pressed',
            linkPosition,
            linkText,
            linkAction,
          });
          break;
        }

        case YearsOfExperienceActions.YEARS_OF_EXPERIENCE_EDIT_LOADED: {
          const { currentPageSectionState } = action.payload;
          yearsOfExperienceEditLoaded({
            currentPageSectionState,
          });

          break;
        }

        default: {
          break;
        }
      }

      return next(action);
    });
